import { ApiRequestError } from "./apiRequestError";
import { formatExecutionCandleTypeLabel } from "./executionCandleTimeframe";
import { strategyRdConceptDisplayName } from "./strategyRdConceptCopy";
import type { StrategyRdPineScript, StrategyRdRobustness } from "../types/strategyRd";

/** `pine_notes` + `chart_timeframe` from Confirm and the indicator export. */
export type StrategyRdPineExportInfo = {
  /** Plain-language differences TradingView forces (show under the Pine code). */
  pineNotes: string[];
  /** The chart the script must run on (finest TF in the recipe), e.g. "5m". */
  chartTimeframe: string | null;
  /** Confirm's `pinescripts` — one TradingView script per side, or one for both. */
  scripts?: StrategyRdPineScript[];
  /** Confirm's automatic robustness test (queued) — the strategy page follows it. */
  robustness?: StrategyRdRobustness | null;
  /** Backtest's `parity.tv_symbol` — where to paste the scripts. */
  tvSymbol?: string | null;
};

export const STRATEGY_RD_PINE_NOT_TRANSLATABLE = "strategy_rd.pine_not_translatable";

export function parseStrategyRdPineExportInfo(raw: unknown): StrategyRdPineExportInfo | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  const pineNotes = Array.isArray(row.pine_notes)
    ? row.pine_notes.map((n) => (typeof n === "string" ? n.trim() : "")).filter(Boolean)
    : [];
  const chartTimeframe =
    typeof row.chart_timeframe === "string" && row.chart_timeframe.trim()
      ? row.chart_timeframe.trim()
      : null;
  if (pineNotes.length === 0 && !chartTimeframe) return null;
  return { pineNotes, chartTimeframe };
}

/** "Add to a 5m chart." — the script stops with a message on any other timeframe. */
export function strategyRdPineChartTimeframeHint(chartTimeframe: string | null | undefined): string | null {
  const tf = (chartTimeframe || "").trim();
  if (!tf) return null;
  return `Add to a ${formatExecutionCandleTypeLabel(tf) || tf} chart.`;
}

const storageKey = (strategyId: string) => `strategyRd.pineExport.${strategyId.trim()}`;

/**
 * Confirm redirects to the strategy page; the notes are only on the Confirm result,
 * so hand them over for that session.
 */
export function saveStrategyRdPineExportInfo(
  strategyId: string,
  info: StrategyRdPineExportInfo | null
): void {
  if (!strategyId.trim() || !info) return;
  try {
    window.sessionStorage.setItem(storageKey(strategyId), JSON.stringify(info));
  } catch {
    // Storage blocked — the panel just won't show the notes.
  }
}

export function loadStrategyRdPineExportInfo(strategyId: string): StrategyRdPineExportInfo | null {
  if (!strategyId.trim()) return null;
  try {
    const raw = window.sessionStorage.getItem(storageKey(strategyId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StrategyRdPineExportInfo>;
    return {
      pineNotes: Array.isArray(parsed.pineNotes)
        ? parsed.pineNotes.filter((n): n is string => typeof n === "string" && Boolean(n.trim()))
        : [],
      chartTimeframe: typeof parsed.chartTimeframe === "string" ? parsed.chartTimeframe : null,
      scripts: Array.isArray(parsed.scripts)
        ? parsed.scripts.filter(
            (x): x is StrategyRdPineScript =>
              Boolean(x) && typeof x.script === "string" && typeof x.side === "string"
          )
        : [],
      robustness:
        parsed.robustness && typeof parsed.robustness === "object" ? parsed.robustness : null,
      tvSymbol: typeof parsed.tvSymbol === "string" && parsed.tvSymbol.trim() ? parsed.tvSymbol : null,
    };
  } catch {
    return null;
  }
}

/** Save-as-strategy failed on the Pine step: `details.missing[]` → readable lines. */
export function strategyRdPineNotTranslatableMissing(err: unknown): string[] | null {
  if (!(err instanceof ApiRequestError) || err.slug !== STRATEGY_RD_PINE_NOT_TRANSLATABLE) {
    return null;
  }
  const missing = err.details?.missing;
  if (!Array.isArray(missing)) return [];
  const out: string[] = [];
  for (const item of missing) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const id = typeof row.id === "string" ? row.id.trim() : "";
    const kind = typeof row.kind === "string" ? row.kind.trim() : "";
    const reason = typeof row.reason === "string" ? row.reason.trim() : "";
    const name = id ? strategyRdConceptDisplayName(id) : kind || "Part of the recipe";
    const what = kind && id ? `${name} (${kind})` : name;
    out.push(reason ? `${what}: ${reason}` : what);
  }
  return out;
}

const SCRIPT_TITLES: Record<string, string> = {
  long: "Longs script",
  short: "Shorts script",
};

/** Copy-box title: "Longs script" / "Shorts script" / "Script". */
export function strategyRdPineScriptTitle(side: string): string {
  return SCRIPT_TITLES[side] ?? "Script";
}

/**
 * Scripts to show: the strategy's saved `pinescript_by_side` (two-sided recipes),
 * else Confirm's `pinescripts` from this session. Longs first.
 */
export function strategyRdPineScriptsForDisplay(opts: {
  bySide?: Record<string, string> | null;
  confirmScripts?: StrategyRdPineScript[] | null;
}): StrategyRdPineScript[] {
  const bySide = opts.bySide ?? {};
  const saved = Object.entries(bySide).map(([side, script]) => ({ side, script }));
  const list = saved.length > 0 ? saved : opts.confirmScripts ?? [];
  const order = (side: string) => (side === "long" ? 0 : side === "short" ? 1 : 2);
  return [...list].filter((x) => x.script.trim()).sort((a, b) => order(a.side) - order(b.side));
}
